import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import MainLayout from '../components/layout/MainLayout';
import AgendaSection from '../components/ui/AgendaSection';
import PointageModule from '../components/shared/PointageModule';
import MarquerFinJournee from '../components/MarquerFinJournee';
import PersonnelManagementModule from '../components/shared/PersonnelManagementModule';
import { TaskManagementModule } from '../components/rh';
import MaPerformance from '../components/rh/MaPerformance';
import PreuvesPanel from '../components/shared/PreuvesPanel';
import ObjectifsEmployesModule from '../components/shared/ObjectifsEmployesModule';
import MesDemandesPermission from '../components/employe/MesDemandesPermission';
import useDeptDashboard from '../hooks/useDeptDashboard';
import CRMModule from '../components/projets/CRMModule';
import './DashboardDGPage.css';
import { useMobileSidebar } from '../hooks/useMobileSidebar';

const TODAY = new Date().toISOString().slice(0, 10);
const DEPT_ID = 19;

function Pbar({ pct, color }) {
  const c = color || (pct >= 70 ? '#4caf50' : pct >= 40 ? '#ff9800' : '#ef5350');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div className="pbar-wrap" style={{ flex: 1 }}>
        <div className="pbar-fill" style={{ width: pct + '%', background: c }} />
      </div>
      <span style={{ fontSize: 10, color: '#778', width: 28, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

function ModalWrap({ title, onSave, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,.12)', borderRadius: 14, padding: 28, minWidth: 440, maxWidth: 560, width: '92%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#889', fontSize: 22, cursor: 'pointer' }}>X</button>
        </div>
        {children}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn-outline" onClick={onClose}>Annuler</button>
          <button className="btn-gold" onClick={onSave}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, name, type = 'text', opts, rows, required, form, setForm }) => {
  const handleChange = (e) => setForm(prev => ({ ...prev, [name]: e.target.value }));
  const s = { width: '100%', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontFamily: 'inherit', fontSize: 13, outline: 'none' };
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 10, color: '#667', textTransform: 'uppercase', letterSpacing: '.8px', display: 'block', marginBottom: 5 }}>
        {label} {required && <span style={{ color: '#ef8080' }}>*</span>}
      </label>
      {opts
        ? <select style={{ ...s, background: '#0d1b2a', border: '1px solid rgba(255,255,255,.15)' }} value={form[name] || ''} onChange={handleChange}>
            <option value="">-- choisir --</option>
            {opts.map(o => <option key={o.v || o} value={o.v || o} style={{ background: '#0d1b2a' }}>{o.l || o}</option>)}
          </select>
        : rows
        ? <textarea style={{ ...s, resize: 'vertical' }} rows={rows} value={form[name] || ''} onChange={handleChange} />
        : <input style={s} type={type} value={form[name] || ''} onChange={handleChange} />
      }
    </div>
  );
};

function DashboardProjetsPage() {
  const navigate = useNavigate();
  useMobileSidebar();
  const { logout: authLogout, user } = useAuth();
  const [activePage, setActivePage] = useState('vue-globale');
  const [now, setNow] = useState(new Date());
  const [projets, setProjets] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [taskFilter, setTaskFilter] = useState('toutes');
  const [modalJustif, setModalJustif] = useState(null);
  const [justifText, setJustifText] = useState('');
  const [justifNiveau, setJustifNiveau] = useState('100');
  const [preuveFiles, setPreuveFiles] = useState([]);

  useEffect(() => {
    api.projects.getAll({ departement_id: DEPT_ID }).then(res => setProjets(res.projects || [])).catch(() => {});
  }, []);

  const { employes, setEmployes, tasks, setTasks, loading, error, refetch, toggleTask, deleteTask, createTask, updateTask } = useDeptDashboard({ deptId: DEPT_ID });
  const allObj = employes.flatMap(e => e.objectifs || []);
  const totalTasks = employes.reduce((sum, emp) => sum + (emp.assignedTasks || []).length, 0);

  const myTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];
    const userId = user?.id;
    // employee_id dans mapTask = ID de l'employé (pas user)
    // On cherche l'employé dont le user_id correspond à l'utilisateur connecté
    const myEmployee = employes.find(e => e.user_id === userId);
    const myEmployeeId = myEmployee?.id;
    if (myEmployeeId) {
      const mine = tasks.filter(t => t.employee_id === myEmployeeId || t.employee_id === userId);
      if (mine.length > 0) return mine;
    }
    // Fallback : filtrer par user_id directement
    if (userId) {
      const mine = tasks.filter(t => t.employee_id === userId);
      if (mine.length > 0) return mine;
    }
    return [];
  }, [tasks, employes, user]);

  const pendingTasks = myTasks.filter(t => !t.done && !t.completed).length;
  const presents = employes.filter(e => e.present).length;

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const openJustif = useCallback((t) => { setModalJustif({ taskId: t.id, titre: t.titre || t.text }); setJustifText(''); setJustifNiveau('100'); setPreuveFiles([]); }, []);

  const saveJustif = async () => {
    if (!justifText.trim()) { alert('Veuillez décrire ce que vous avez réalisé.'); return; }
    if (preuveFiles.length === 0) { alert('Veuillez joindre au moins un document de preuve.'); return; }
    try {
      await Promise.all(preuveFiles.map(f => api.tasks.uploadProof(modalJustif.taskId, f)));
      await toggleTask(modalJustif.taskId);
      setModalJustif(null);
      setPreuveFiles([]);
      setJustifText('');
    } catch (err) {
      alert('Erreur lors de la validation : ' + (err.message || 'Erreur inconnue'));
    }
  };

  const openModal = useCallback((type, data = {}) => { setForm(data); setModal(type); }, []);
  const closeModal = useCallback(() => { setModal(null); setForm({}); }, []);
  const handleLogout = async () => { await authLogout(); navigate('/auth', { replace: true }); };
  const formattedDate = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const projetsEnCours = projets.filter(p => p.status === 'en_cours').length;
  const projetsPlanif = projets.filter(p => p.status === 'planification').length;
  const projetsTermines = projets.filter(p => p.status === 'termine').length;
  const avgProgress = projets.length > 0 ? Math.round(projets.reduce((s, p) => s + (p.progress || 0), 0) / projets.length) : 0;
  const projetsEnRetard = projets.filter(p => p.status === 'en_cours' && (p.progress || 0) < 40).length;

  const saveTask = async () => {
    if (!form.text) return;
    if (form._editId) {
      try {
        const orig = tasks.find(t => t.id === form._editId);
        await updateTask(form._editId, { titre: form.text, priority: form.prio || 'normale', employee_id: orig?.employee_id });
      } catch {}
    } else {
      const myEmployee = employes.find(e => e.user_id === user?.id);
      const myEmployeeId = myEmployee?.id ?? null;
      try {
        await createTask({ titre: form.text, priority: form.prio || 'normale', category: 'Autre', employee_id: myEmployeeId });
      } catch {
        setTasks(prev => [...prev, { id: Date.now(), text: form.text, done: false, prio: form.prio || 'normale', date: TODAY, employee_id: myEmployeeId }]);
      }
    }
    closeModal();
  };

  const statusColor = { en_cours: '#42a5f5', planification: '#C9A84C', production: '#ff9800', termine: '#66bb6a', annule: '#ef5350', negociation: '#ab47bc' };
  const statusLabel = { en_cours: 'En cours', planification: 'Planification', production: 'Production', termine: 'Termine', annule: 'Annule', negociation: 'Negociation' };

  return (
    <MainLayout>
      <div className="dashboard-dg-root">

        {/* TOPBAR */}
        <div className="topbar">
          <div className="topbar-left">
            <div className="tlogo" style={{ background: 'linear-gradient(135deg,#ab47bc,#7b1fa2)' }}>📋</div>
            <div>
              <div className="tname">FHC Conseil — Gestion de Projets</div>
              <div className="tsub">Dashboard — Chef Département Gestion des Projets</div>
            </div>
          </div>
          <div className="topbar-right">
            <div className="tdate">{formattedDate}</div>
            <button className="tbtn" onClick={refetch}>🔄</button>
            <div style={{ textAlign: 'right' }}>
              <div className="tuser">Chef Projets</div>
              <div className="trole" style={{ color: '#ab47bc' }}>📁 Gestion des Projets</div>
            </div>
            <div className="tavatar" style={{ background: 'linear-gradient(135deg,#ab47bc,#7b1fa2)' }}>CP</div>
            <button className="tbtn" onClick={handleLogout} style={{ color: '#ef8080', borderColor: 'rgba(239,83,80,.3)' }}>🚪 Déconnexion</button>
          </div>
        </div>

        {/* LAYOUT */}
        <div className="layout">

          {/* SIDEBAR */}
          <div className="sidebar">
            <div className="sb-logo">Gestion de Projets</div>
            {[
              { id: 'vue-globale', icon: '🏠', label: 'Vue globale' },
              { id: 'projets', icon: '📁', label: 'Projets', badge: String(projets.length) },
              { id: 'crm', icon: '🤝', label: 'CRM' },
              { id: 'stats', icon: '📊', label: 'Statistiques' },
              { id: 'personnel', icon: '👥', label: 'Personnel', badge: String(employes.length), badgeCls: 'green' },
            ].map(it => (
              <div key={it.id} className={`sb-item ${activePage === it.id ? 'active' : ''}`} onClick={() => setActivePage(it.id)}>
                <span className="si">{it.icon}</span><span>{it.label}</span>
                {it.badge && <span className={`sb-badge ${it.badgeCls || ''}`}>{it.badge}</span>}
              </div>
            ))}
            <div className="sb-sep" />
            <div className="sb-logo">Equipe et Pilotage</div>
            {[
              { id: 'mon-equipe', icon: '👤', label: 'Mon équipe', badge: String(employes.length), badgeCls: 'green' },
              { id: 'ma-performance', icon: '⭐', label: 'Ma Performance' },
              { id: 'gestion-taches', icon: '📝', label: 'Gestion Tâches', badge: totalTasks > 0 ? String(totalTasks) : '', badgeCls: 'gold' },
              { id: 'taches', icon: '✅', label: 'Mes tâches', badge: pendingTasks > 0 ? String(pendingTasks) : '' },
              { id: 'objectifs', icon: '🎯', label: 'Objectifs', badge: allObj.filter(o => o.statut === 'atteint').length > 0 ? String(allObj.filter(o => o.statut === 'atteint').length) : '' },
              { id: 'agenda', icon: '📅', label: 'Agenda' },
              { id: 'permissions', icon: '🗂️', label: 'Mes Permissions' },
              { id: 'rapports', icon: '📈', label: 'Rapports' },
            ].map(it => (
              <div key={it.id} className={`sb-item ${activePage === it.id ? 'active' : ''}`} onClick={() => setActivePage(it.id)}>
                <span className="si">{it.icon}</span><span>{it.label}</span>
                {it.badge && <span className={`sb-badge ${it.badgeCls || ''}`}>{it.badge}</span>}
              </div>
            ))}
            <div className="sb-sep" />
            <div className="sb-item" onClick={() => navigate('/documents')}>
              <span className="si">📁</span><span>Documents</span>
            </div>
          </div>

          {/* MAIN */}
          <div className="main">
            {loading && <div style={{ padding: 20, textAlign: 'center', color: '#C9A84C', fontSize: 13 }}>Chargement...</div>}
            {error && !loading && <div style={{ margin: '10px 0', padding: '8px 14px', background: 'rgba(239,83,80,.1)', border: '1px solid rgba(239,83,80,.3)', borderRadius: 8, fontSize: 12, color: '#ef8080' }}>Erreur : {error}</div>}

            {/* VUE GLOBALE */}
            <div className={`page ${activePage === 'vue-globale' ? 'active' : ''}`}>
              <div className="page-header">
                <div>
                  <div className="page-title">Vue Globale - Gestion des Projets</div>
                  <div className="page-sub">FHC Conseil - {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <button className="btn-gold" onClick={() => setActivePage('projets')}>+ Nouveau projet</button>
              </div>
              <PointageModule currentUser={{ nom: 'Chef Projets' }} />
              <MarquerFinJournee onSuccess={() => {}} />
              <div className="kpi-grid">
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#ab47bc' }} /><div className="kpi-icon">📁</div><div className="kpi-val">{projets.length}</div><div className="kpi-label">Total projets</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#42a5f5' }} /><div className="kpi-icon">🔄</div><div className="kpi-val" style={{ color: '#42a5f5' }}>{projetsEnCours}</div><div className="kpi-label">En cours</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#C9A84C' }} /><div className="kpi-icon">🗓️</div><div className="kpi-val" style={{ color: '#C9A84C' }}>{projetsPlanif}</div><div className="kpi-label">En planification</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#66bb6a' }} /><div className="kpi-icon">✅</div><div className="kpi-val" style={{ color: '#66bb6a' }}>{projetsTermines}</div><div className="kpi-label">Terminés</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#ef5350' }} /><div className="kpi-icon">⚠️</div><div className="kpi-val" style={{ color: '#ef8080' }}>{projetsEnRetard}</div><div className="kpi-label">En retard</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#26c6da' }} /><div className="kpi-icon">📊</div><div className="kpi-val" style={{ color: '#26c6da' }}>{avgProgress}%</div><div className="kpi-label">Avancement moyen</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#4caf50' }} /><div className="kpi-icon">👥</div><div className="kpi-val">{employes.length}</div><div className="kpi-label">Membres équipe</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#ff9800' }} /><div className="kpi-icon">📝</div><div className="kpi-val" style={{ color: '#ffb74d' }}>{pendingTasks}</div><div className="kpi-label">Tâches en attente</div></div>
              </div>
              <div className="grid2">
                <div className="card">
                  <div className="card-title">Projets recents
                    <button className="btn-outline" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setActivePage('projets')}>Voir tout</button>
                  </div>
                  {projets.length === 0
                    ? <div style={{ padding: '16px 0', textAlign: 'center', color: '#445', fontSize: 12 }}>Aucun projet charge.</div>
                    : projets.slice(0, 5).map(p => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: '#fff' }}>{p.name}</div>
                          <div style={{ fontSize: 9, color: '#556' }}>{p.client || 'Client'} - {p.filiale?.nom || 'FHC'}</div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: (statusColor[p.status] || '#888') + '22', color: statusColor[p.status] || '#888' }}>
                          {statusLabel[p.status] || p.status}
                        </span>
                      </div>
                    ))
                  }
                </div>
                <div className="card">
                  <div className="card-title">Taches prioritaires
                    <button className="btn-outline" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setActivePage('taches')}>Voir tout</button>
                  </div>
                  {myTasks.filter(t => !(t.done || t.completed)).length === 0
                    ? <div style={{ padding: '16px 0', textAlign: 'center', color: '#445', fontSize: 12 }}>Aucune tache en attente.</div>
                    : myTasks.filter(t => !(t.done || t.completed)).slice(0, 5).map(t => {
                        const p = t.priority || t.prio || 'normale';
                        return (
                          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 500, color: '#fff' }}>{t.titre || t.text}</div>
                              <div style={{ fontSize: 9, color: '#556' }}>{t.category || 'Autre'}</div>
                            </div>
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: p === 'urgente' ? 'rgba(239,83,80,.15)' : p === 'haute' ? 'rgba(255,152,0,.15)' : 'rgba(66,165,245,.15)', color: p === 'urgente' ? '#ef8080' : p === 'haute' ? '#ffb74d' : '#64b5f6' }}>
                              {p === 'urgente' ? 'Urgente' : p === 'haute' ? 'Haute' : p === 'normale' ? 'Normale' : 'Basse'}
                            </span>
                          </div>
                        );
                      })
                  }
                </div>
              </div>
              <div className="grid2">
                <div className="card">
                  <div className="card-title">Alertes a traiter</div>
                  {projetsEnRetard === 0 && myTasks.filter(t => !(t.done || t.completed) && ((t.priority || t.prio) === 'urgente' || (t.priority || t.prio) === 'haute')).length === 0
                    ? <div style={{ padding: '16px 0', textAlign: 'center', color: '#66bb6a', fontSize: 12 }}>Aucune alerte</div>
                    : <>
                      {myTasks.filter(t => !(t.done || t.completed) && ((t.priority || t.prio) === 'urgente' || (t.priority || t.prio) === 'haute')).map(t => (
                        <div key={t.id} style={{ display: 'flex', gap: 9, padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef5350', flexShrink: 0, marginTop: 3 }} />
                          <div style={{ fontSize: 11, color: '#ccc' }}><strong>{t.titre || t.text}</strong> - Tache {t.priority || t.prio}</div>
                        </div>
                      ))}
                      {projets.filter(p => p.status === 'en_cours' && (p.progress || 0) < 40).map(p => (
                        <div key={p.id} style={{ display: 'flex', gap: 9, padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#ff9800', flexShrink: 0, marginTop: 3 }} />
                          <div style={{ fontSize: 11, color: '#ccc' }}><strong>{p.name}</strong> - En retard ({p.progress || 0}% avance)</div>
                        </div>
                      ))}
                    </>
                  }
                </div>
                <div className="card">
                  <div className="card-title">Repartition des projets</div>
                  {projets.length === 0
                    ? <div style={{ padding: '16px 0', textAlign: 'center', color: '#445', fontSize: 12 }}>Aucun projet.</div>
                    : ['planification', 'en_cours', 'termine', 'annule'].map(status => {
                        const n = projets.filter(p => p.status === status).length;
                        if (n === 0) return null;
                        return (
                          <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <div style={{ fontSize: 10, color: '#aaa', width: 100 }}>{statusLabel[status]}</div>
                            <div style={{ flex: 1 }}><Pbar pct={Math.round(n / projets.length * 100)} color={statusColor[status]} /></div>
                            <div style={{ fontSize: 10, fontWeight: 600, width: 16, color: statusColor[status] }}>{n}</div>
                          </div>
                        );
                      })
                  }
                </div>
              </div>
            </div>

            {/* PROJETS */}
            <div className={`page ${activePage === 'projets' ? 'active' : ''}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Projets du departement</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{projets.length} projet{projets.length !== 1 ? 's' : ''}</div>
              </div>
              {projets.length === 0
                ? <div style={{ textAlign: 'center', padding: 40, color: '#445' }}>Aucun projet assigne a ce departement.</div>
                : projets.map(p => {
                    const sc = statusColor[p.status] || '#888';
                    const sl = statusLabel[p.status] || p.status;
                    const av = p.progress || 0;
                    return (
                      <div key={p.id} style={{ background: '#162535', borderRadius: 10, padding: 16, marginBottom: 12, border: '1px solid rgba(255,255,255,.06)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>{p.name}</div>
                            <div style={{ fontSize: 11, color: '#667' }}>{p.filiale?.nom || ''}</div>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: sc + '22', color: sc }}>{sl}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                          {[['Budget', p.budget ? Number(p.budget).toLocaleString('fr-FR') + ' FCFA' : '-'], ['Deadline', p.end_date ? new Date(p.end_date).toLocaleDateString('fr-FR') : '-'], ['Chef', p.owner ? (p.owner.prenom + ' ' + (p.owner.nom || '')) : '-']].map(([l, v]) => (
                            <div key={l} style={{ background: 'rgba(255,255,255,.04)', borderRadius: 8, padding: '8px 10px' }}>
                              <div style={{ fontSize: 9, color: '#556', marginBottom: 3 }}>{l}</div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#ccc' }}>{v}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize: 10, color: '#667', marginBottom: 5, display: 'flex', justifyContent: 'space-between' }}>
                          <span>Avancement</span><span style={{ fontWeight: 600 }}>{av}%</span>
                        </div>
                        <div style={{ height: 5, background: 'rgba(255,255,255,.06)', borderRadius: 99 }}>
                          <div style={{ width: av + '%', height: '100%', background: av >= 70 ? '#4caf50' : av >= 40 ? '#ff9800' : '#ef5350', borderRadius: 99 }} />
                        </div>
                      </div>
                    );
                  })
              }
            </div>

            {/* CRM */}
            <div className={`page ${activePage === 'crm' ? 'active' : ''}`}>
              <CRMModule />
            </div>

            {/* STATISTIQUES */}
            <div className={`page ${activePage === 'stats' ? 'active' : ''}`}>
              <div className="page-header"><div><div className="page-title">Statistiques - Gestion des Projets</div></div></div>
              <div className="kpi-grid">
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#ab47bc' }} /><div className="kpi-icon">📁</div><div className="kpi-val">{projets.length}</div><div className="kpi-label">Total projets</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#26c6da' }} /><div className="kpi-icon">📊</div><div className="kpi-val" style={{ color: '#26c6da' }}>{avgProgress}%</div><div className="kpi-label">Avancement moyen</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#66bb6a' }} /><div className="kpi-icon">✅</div><div className="kpi-val" style={{ color: '#66bb6a' }}>{projetsTermines}</div><div className="kpi-label">Terminés</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#ef5350' }} /><div className="kpi-icon">⚠️</div><div className="kpi-val" style={{ color: '#ef8080' }}>{projetsEnRetard}</div><div className="kpi-label">En retard</div></div>
              </div>
              <div className="card">
                <div className="card-title">Repartition par statut</div>
                {['planification', 'en_cours', 'production', 'termine', 'annule', 'negociation'].map(status => {
                  const n = projets.filter(p => p.status === status).length;
                  if (n === 0) return null;
                  return (
                    <div key={status} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#aaa' }}>{statusLabel[status]}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: statusColor[status] }}>{n} projet{n > 1 ? 's' : ''}</span>
                      </div>
                      <Pbar pct={projets.length > 0 ? Math.round(n / projets.length * 100) : 0} color={statusColor[status]} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* PERSONNEL */}
            <div className={`page ${activePage === 'personnel' ? 'active' : ''}`}>
              <PersonnelManagementModule employes={employes} setEmployes={setEmployes} departmentName="Gestion de Projets" heureLimite="08:30" deptId={DEPT_ID} />
            </div>

            {/* MON EQUIPE */}
            <div className={`page ${activePage === 'mon-equipe' ? 'active' : ''}`}>
              <div className="page-header">
                <div><div className="page-title">Mon equipe - Projets</div><div className="page-sub">{employes.length} collaborateur(s)</div></div>
              </div>
              <div className="kpi-grid">
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#42a5f5' }} /><div className="kpi-icon">👥</div><div className="kpi-val">{employes.length}</div><div className="kpi-label">Collaborateurs</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#4caf50' }} /><div className="kpi-icon">✅</div><div className="kpi-val" style={{ color: '#66bb6a' }}>{presents}</div><div className="kpi-label">Présents</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#ef5350' }} /><div className="kpi-icon">❌</div><div className="kpi-val" style={{ color: '#ef8080' }}>{employes.length - presents}</div><div className="kpi-label">Absents</div></div>
                <div className="kpi-card"><div className="kpi-accent" style={{ background: '#C9A84C' }} /><div className="kpi-icon">⭐</div><div className="kpi-val" style={{ color: '#C9A84C' }}>{employes.length > 0 ? (employes.reduce((s, e) => s + (e.score || 0), 0) / employes.length).toFixed(1) : '-'}</div><div className="kpi-label">Score moyen</div></div>
              </div>
              <div className="card">
                <table className="tbl">
                  <thead><tr><th>Collaborateur</th><th>Poste</th><th>Contrat</th><th>Presence</th><th>Score</th></tr></thead>
                  <tbody>
                    {employes.map(e => (
                      <tr key={e.id}>
                        <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div style={{ width: 28, height: 28, borderRadius: '50%', background: (e.color || '#42a5f5') + '22', color: e.color || '#42a5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{e.av}</div><div><div className="bold">{e.prenom} {e.nom}</div><div style={{ fontSize: 9, color: '#556' }}>Depuis {e.entree}</div></div></div></td>
                        <td>{e.poste}</td>
                        <td><span className={`badge ${e.contrat === 'CDI' ? 'gold' : e.contrat === 'CDD' ? 'blue' : 'purple'}`}>{e.contrat}</span></td>
                        <td>{e.present ? <span className="badge green">✅ Présent</span> : <span className="badge red">❌ Absent</span>}</td>
                        <td><span style={{ fontFamily: 'Cormorant Garamond,serif', fontSize: 18, fontWeight: 600, color: e.score >= 8 ? '#66bb6a' : e.score >= 6 ? '#C9A84C' : '#ef8080' }}>{e.score || '-'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* MA PERFORMANCE */}
            <div className={`page ${activePage === 'ma-performance' ? 'active' : ''}`}>
              <div className="page-header"><div><div className="page-title">⭐ Ma Performance</div><div className="page-sub">Consultez votre performance personnelle</div></div></div>
              <MaPerformance />
            </div>

            {/* GESTION TACHES */}
            <div className={`page ${activePage === 'gestion-taches' ? 'active' : ''}`}>
              <div className="page-header"><div><div className="page-title">📝 Gestion des Tâches</div><div className="page-sub">{totalTasks} tache{totalTasks !== 1 ? 's' : ''} assignee{totalTasks !== 1 ? 's' : ''}</div></div></div>
              <PreuvesPanel tasks={tasks} employes={employes} />
              <TaskManagementModule employes={employes} setEmployes={setEmployes} />
            </div>

            {/* MES TACHES */}
            <div className={`page ${activePage === 'taches' ? 'active' : ''}`}>
              <div className="page-header">
                <div><div className="page-title">Mes Tâches</div><div className="page-sub">{pendingTasks} en attente</div></div>
                <button className="btn-gold" onClick={() => openModal('tache', { prio: 'normale' })}>+ Ajouter</button>
              </div>
              {myTasks.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  {[
                    { v: 'toutes', l: 'Toutes', count: myTasks.length },
                    { v: 'en_attente', l: 'En attente', count: myTasks.filter(t => !(t.done || t.completed)).length },
                    { v: 'terminees', l: 'Terminees', count: myTasks.filter(t => t.done || t.completed).length },
                  ].map(f => (
                    <button
                      key={f.v}
                      onClick={() => setTaskFilter(f.v)}
                      style={{
                        fontSize: 11, padding: '5px 12px', borderRadius: 99,
                        border: '1.5px solid ' + (taskFilter === f.v ? '#ab47bc' : 'rgba(255,255,255,.07)'),
                        background: taskFilter === f.v ? 'rgba(171,71,188,.1)' : 'transparent',
                        color: taskFilter === f.v ? '#ce93d8' : '#667',
                        cursor: 'pointer',
                      }}
                    >
                      {f.l} ({f.count})
                    </button>
                  ))}
                </div>
              )}
              <div className="card">
                {myTasks.length === 0
                  ? <div style={{ padding: '20px 0', textAlign: 'center', color: '#445', fontSize: 12 }}>Aucune tâche.</div>
                  : (() => {
                      const filtered = myTasks.filter(t => {
                        const isDone = t.done || t.completed;
                        if (taskFilter === 'en_attente') return !isDone;
                        if (taskFilter === 'terminees') return isDone;
                        return true;
                      });
                      if (filtered.length === 0) return <div style={{ padding: '20px 0', textAlign: 'center', color: '#445', fontSize: 12 }}>Aucune tache dans cette categorie.</div>;
                      return filtered.map(t => {
                        const isDone = t.done || t.completed;
                        const prio = t.priority || t.prio || 'normale';
                        const prioBg = prio === 'urgente' ? 'rgba(239,83,80,.15)' : prio === 'haute' ? 'rgba(255,152,0,.15)' : 'rgba(66,165,245,.15)';
                        const prioColor = prio === 'urgente' ? '#ef8080' : prio === 'haute' ? '#ffb74d' : '#64b5f6';
                        const prioLabel = prio === 'urgente' ? 'Urgente' : prio === 'haute' ? 'Haute' : prio === 'normale' ? 'Normale' : 'Basse';
                        return (
                          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                            <div
                              onClick={() => !isDone && openJustif(t)}
                              style={{ width: 18, height: 18, borderRadius: 4, border: '1.5px solid ' + (isDone ? '#4caf50' : '#445'), background: isDone ? '#4caf50' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'white', cursor: isDone ? 'default' : 'pointer', flexShrink: 0 }}
                            >{isDone ? 'V' : ''}</div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, color: isDone ? '#445' : '#ccc', textDecoration: isDone ? 'line-through' : 'none' }}>{t.titre || t.text}</div>
                              {t.category && <div style={{ fontSize: 10, color: '#556', marginTop: 2 }}>{t.category}</div>}
                            </div>
                            <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: prioBg, color: prioColor, whiteSpace: 'nowrap' }}>{prioLabel}</span>
                            {!isDone && <button style={{ background: 'none', border: 'none', color: '#64b5f6', cursor: 'pointer', fontSize: 13, padding: '0 4px' }} onClick={() => openModal('tache', { _editId: t.id, text: t.titre || t.text, prio: t.priority || t.prio || 'normale' })}>✏️</button>}
                            <button style={{ background: 'none', border: 'none', color: '#ef5350', cursor: 'pointer', fontSize: 14, padding: '0 4px' }} onClick={() => deleteTask(t.id)}>X</button>
                          </div>
                        );
                      });
                    })()
                }
              </div>
            </div>

            {/* OBJECTIFS */}
            <div className={`page ${activePage === 'objectifs' ? 'active' : ''}`}>
              <div className="page-header"><div><div className="page-title">🎯 Objectifs de l'équipe</div><div className="page-sub">{allObj.length} objectif{allObj.length !== 1 ? 's' : ''}</div></div></div>
              <ObjectifsEmployesModule employes={employes} onRefresh={refetch} isChef={true} />
            </div>

            {/* AGENDA */}
            <div className={`page ${activePage === 'agenda' ? 'active' : ''}`}>
              <div className="page-header"><div><div className="page-title">📅 Agenda — Rendez-vous</div><div className="page-sub">Réunions · Suivis · RDV clients</div></div></div>
              <AgendaSection filterTypes={['client', 'suivi', 'commercial', 'partenariat', 'equipe', 'codir', 'urgente', 'autre']} />
            </div>

            {/* PERMISSIONS */}
            <div className={`page ${activePage === 'permissions' ? 'active' : ''}`}>
              <div className="page-header"><div><div className="page-title">🗂️ Mes Demandes de Permission</div><div className="page-sub">Gérez vos demandes de congés, absences et permissions</div></div></div>
              <MesDemandesPermission />
            </div>

            {/* RAPPORTS */}
            <div className={`page ${activePage === 'rapports' ? 'active' : ''}`}>
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                <h3>📈 Section Rapports</h3>
                <p>Disponible prochainement.</p>
              </div>
            </div>

          </div>{/* /main */}
        </div>{/* /layout */}

        {/* MODAL JUSTIF VALIDATION */}
        {modalJustif && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setModalJustif(null)}>
            <div style={{ background: '#0d1825', border: '1px solid rgba(255,255,255,.06)', borderRadius: 12, padding: 22, width: 500, maxHeight: '88vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                📎 Valider la tâche
                <button onClick={() => setModalJustif(null)} style={{ background: 'none', border: 'none', color: '#667', fontSize: 16, cursor: 'pointer' }}>✕</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.7px', textTransform: 'uppercase', color: '#8a9bb0' }}>Tâche</label>
                  <input value={modalJustif?.titre || ''} readOnly style={{ marginTop: 4, background: 'rgba(255,255,255,.03)', border: '1.5px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '9px 11px', color: '#aaa', fontSize: 12, outline: 'none', width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.7px', textTransform: 'uppercase', color: '#8a9bb0' }}>Ce que j'ai réalisé *</label>
                  <textarea rows={3} placeholder="Décrivez concrètement ce que vous avez accompli..." value={justifText} onChange={e => setJustifText(e.target.value)} style={{ marginTop: 4, background: 'rgba(255,255,255,.05)', border: '1.5px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '9px 11px', color: '#fff', fontSize: 12, outline: 'none', width: '100%', resize: 'vertical' }} />
                </div>
                <div>
                  <label style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.7px', textTransform: 'uppercase', color: '#8a9bb0' }}>Niveau d'accomplissement</label>
                  <select value={justifNiveau} onChange={e => setJustifNiveau(e.target.value)} style={{ marginTop: 4, background: '#1a2a3a', border: '1.5px solid rgba(255,255,255,.1)', borderRadius: 7, padding: '9px 11px', color: '#fff', fontSize: 12, outline: 'none', width: '100%' }}>
                    <option value="100">Complètement réalisée (100%)</option>
                    <option value="75">Largement réalisée (75%)</option>
                    <option value="50">Partiellement réalisée (50%)</option>
                    <option value="25">Débutée (25%)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 9, fontWeight: 600, letterSpacing: '.7px', textTransform: 'uppercase', color: '#8a9bb0' }}>Documents de preuve * <span style={{ color: '#556', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>({preuveFiles.length} fichier{preuveFiles.length !== 1 ? 's' : ''})</span></label>
                  <div onClick={() => document.getElementById('chef-preuve-upload').click()} style={{ marginTop: 6, border: '2px dashed rgba(255,255,255,.15)', borderRadius: 8, padding: '12px', textAlign: 'center', cursor: 'pointer', background: 'rgba(255,255,255,.02)' }}>
                    <input id="chef-preuve-upload" type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" multiple onChange={ev => setPreuveFiles(prev => [...prev, ...Array.from(ev.target.files)])} />
                    <div style={{ fontSize: 11, color: '#667' }}>+ Cliquer pour ajouter un fichier (PDF, Word, Excel, Image)</div>
                  </div>
                  {preuveFiles.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {preuveFiles.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(67,160,71,.08)', border: '1px solid rgba(67,160,71,.25)', borderRadius: 6, padding: '6px 10px' }}>
                          <span style={{ fontSize: 11, color: '#66bb6a', fontWeight: 500 }}>📎 {f.name}</span>
                          <button onClick={() => setPreuveFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#ef5350', cursor: 'pointer', fontSize: 13, padding: '0 2px' }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn-outline" onClick={() => setModalJustif(null)}>Annuler</button>
                <button className="btn-gold" onClick={saveJustif}>✅ Valider</button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL TACHE */}
        {modal === 'tache' && (
          <ModalWrap title={form._editId ? 'Modifier la tâche' : 'Nouvelle tâche'} onSave={saveTask} onClose={closeModal}>
            <Field label="Tache *" name="text" required form={form} setForm={setForm} />
            <Field label="Priorite" name="prio" opts={[{ v: 'urgente', l: 'Urgente' }, { v: 'haute', l: 'Haute' }, { v: 'normale', l: 'Normale' }]} form={form} setForm={setForm} />
          </ModalWrap>
        )}

      </div>
    </MainLayout>
  );
}

export default DashboardProjetsPage;
